import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, MessageSquare, PenLine } from 'lucide-react';
import { apiService } from '@/lib/api';
import { fillTemplate } from '@/lib/emailTemplate';
import { composeSms } from '@/lib/smsTemplate';
import { formatShortDateInCompanyTimeZone, formatTimeInCompanyTimeZone } from '@/lib/dateUtils';
import { useGlobalCompany } from '@/hooks/useGlobalCompany';
import { usePermissions } from '@/hooks/usePermissions';

/** Flows about a scheduled time; ASAP jobs have none, so these are hidden for them. */
const APPOINTMENT_FLOWS = new Set(['appointment_confirmation', 'appointment_reminder']);

const FLOW_LABELS: Record<string, string> = {
    appointment_confirmation: 'Appointment confirmation',
    appointment_reminder: 'Appointment reminder',
    review_request: 'Ask for a review',
    technician_on_the_way: 'Technician on the way',
};

/** What a variable needs, named the way the user would go and fix it. */
const MISSING_REASON: Record<string, string> = {
    customer_name: 'contact name',
    company_name: 'company name',
    job_date: 'a scheduled date',
    technician_name: 'an assigned technician',
    review_link: 'a review link in company settings',
};

/**
 * Structural minimum — matches JobDTO and JobListItemDTO alike. Contacts are deliberately not
 * read from here: the two DTOs nest them differently, and every host already knows which contact
 * it is texting.
 */
export interface SmsTemplateJob {
    id: string;
    date_executed_start?: string | null;
    is_asap?: boolean;
    assigned_technician?: { name?: string | null } | null;
}

interface SmsTemplateMenuProps {
    job: SmsTemplateJob;
    /** Name of the contact this SMS is going to. */
    customerName: string;
    /**
     * Called with the finished message (prefix + filled body + opt-out) ready for SendSmsModal,
     * plus a warning when a variable resolved to nothing and was left out of the text.
     */
    onPickTemplate: (message: string, warning?: string) => void;
    /** Free-text SMS — each host's pre-existing behavior. */
    onPickCustom: () => void;
    /** When set, a back row returns the host dropdown to its own items. */
    onBack?: () => void;
}

const ROW = 'w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors';

/**
 * Clean up after a variable that resolved to nothing: "Hello , your appointment" reads as a bug,
 * "Hello, your appointment" does not.
 *
 * ponytail: only fixes the gap and the punctuation next to it. A template whose missing value ends
 * a clause ("scheduled for ") still trails off — the row warns about that instead.
 */
function tidyGaps(text: string): string {
    return text
        .split('\n')
        .map((line) =>
            line
                .replace(/[ \t]+/g, ' ')
                .replace(/\s+([,.!?;:])/g, '$1')
                .replace(/([,;:])\1+/g, '$1')
                .trim()
        )
        .join('\n');
}

/**
 * The rows of the "SMS Client" submenu: one per SMS template plus a free-text option.
 *
 * Renders rows only — no container, no positioning, no overlay — so it drops into a portaled
 * dropdown, an inline dropdown, or a popover unchanged. Renders nothing when the plan has no SMS
 * or the templates endpoint is unavailable (old gateway), leaving the host's original flow intact.
 */
export function SmsTemplateMenu({ job, customerName, onPickTemplate, onPickCustom, onBack }: SmsTemplateMenuProps) {
    const { selectedCompanyId, selectedCompany, companyTimezone } = useGlobalCompany();
    const { isFeatureAllowed } = usePermissions();
    const canSendSms = isFeatureAllowed('messages.sms');

    const { data: templates, isError } = useQuery({
        queryKey: ['sms-templates', selectedCompanyId],
        queryFn: () => apiService.getSmsTemplates(),
        enabled: canSendSms && !!selectedCompanyId,
        staleTime: 5 * 60 * 1000,
        retry: false,
    });

    const { data: invoiceSettings } = useQuery({
        queryKey: ['company-invoice-settings', selectedCompanyId],
        queryFn: () => apiService.getCompanyInvoiceSettings(),
        enabled: canSendSms && !!selectedCompanyId,
        staleTime: 5 * 60 * 1000,
    });

    if (!canSendSms || isError || !templates?.length) return null;

    const companyName = invoiceSettings?.display_name || selectedCompany?.name || '';
    // ASAP jobs have no agreed time — their date is a placeholder, so leave it out of messages.
    const jobDate = job.date_executed_start && !job.is_asap
        ? [
              formatShortDateInCompanyTimeZone(job.date_executed_start, companyTimezone),
              formatTimeInCompanyTimeZone(job.date_executed_start, companyTimezone),
          ]
              .filter((p) => p && p !== 'N/A')
              .join(', ')
        : '';

    const values: Record<string, string> = {
        customer_name: customerName,
        company_name: companyName,
        job_date: jobDate,
        technician_name: job.assigned_technician?.name ?? '',
        review_link: invoiceSettings?.review_link ?? '',
    };

    return (
        <>
            {onBack && (
                <button type="button" onClick={onBack} className={`${ROW} text-gray-500 hover:bg-gray-50 border-b border-gray-100`}>
                    <ArrowLeft className="h-4 w-4" />
                    Back
                </button>
            )}

            {templates.filter((t) => !(job.is_asap && APPOINTMENT_FLOWS.has(t.flow))).map((t) => {
                // Only variables the body actually uses matter — the supported list is wider.
                const used = [...t.body.matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map((m) => m[1]);
                const missing = [...new Set(used)].filter((v) => !values[v]?.trim());
                // A missing value drops out of the message instead of blocking it; tidyGaps clears
                // the punctuation left behind ("Hello , your" -> "Hello, your").
                const message = composeSms(companyName, tidyGaps(fillTemplate(t.body, values)));

                // The warning belongs on the send screen, where the text is visible and editable.
                const warning = missing.length
                    ? `Missing from this job: ${missing.map((v) => MISSING_REASON[v] ?? v).join(', ')}. ` +
                      'The message was built without it — check the text before sending.'
                    : undefined;

                return (
                    <button
                        key={t.flow}
                        type="button"
                        onClick={() => onPickTemplate(message, warning)}
                        title={message}
                        className={`${ROW} text-blue-600 hover:bg-gray-50`}
                    >
                        <MessageSquare className="h-4 w-4 flex-shrink-0" />
                        <span className="flex-1 min-w-0 truncate">{FLOW_LABELS[t.flow] ?? t.flow}</span>
                    </button>
                );
            })}

            <button type="button" onClick={onPickCustom} className={`${ROW} text-blue-600 hover:bg-gray-50 border-t border-gray-100`}>
                <PenLine className="h-4 w-4" />
                Custom message...
            </button>
        </>
    );
}

export default SmsTemplateMenu;
